import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  Alert, Btn, Tabs,
  colors, spacing, typography, radius,
} from '../design-system';
import {
  useAccreditationSummary,
  useUpcomingSurveillance,
} from '../hooks/useAccreditation';

const QUICK_ACTIONS = [
  { label: 'Manage Cert Bodies',    to: '/accreditation/cert-bodies' },
  { label: 'Manage Auditors',       to: '/accreditation/auditors' },
  { label: 'Manage Courses',        to: '/accreditation/courses' },
  { label: 'Course Accreditations', to: '/accreditation/course-accreditations' },
  { label: 'Surveillance Schedule', to: '/governance/surveillance' },
  { label: 'Accreditation Scopes',  to: '/accreditation/scopes' },
  { label: 'Accreditation Rules',   to: '/accreditation/rules' },
];

const SURVEILLANCE_COLS = [
  { key: 'surveillance_type', label: 'Type',      render: v => v ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—' },
  { key: 'scheduled_date',    label: 'Scheduled', render: v => v ? new Date(v).toLocaleDateString() : '—' },
  { key: 'due_date',          label: 'Due',        render: v => v ? new Date(v).toLocaleDateString() : '—' },
  { key: 'status',            label: 'Status',     render: v => <StatusBadge status={v || 'pending'} /> },
  {
    key: 'id',
    label: 'Action',
    render: () => (
      <Link to="/governance/surveillance" style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none', fontSize: typography.fontSizes.sm }}>
        View →
      </Link>
    ),
  },
];

export default function AccreditationDashboard() {
  const { summary, loading, error } = useAccreditationSummary();
  const { surveillance, loading: survLoading } = useUpcomingSurveillance(30);

  const kpis = summary ? [
    {
      label: 'Certification Bodies',
      value: summary.total_cert_bodies ?? 0,
      subtitle: `${summary.active_cert_bodies ?? 0} active${summary.suspended_cert_bodies > 0 ? ` · ${summary.suspended_cert_bodies} suspended` : ''}`,
      color: colors.primary,
    },
    {
      label: 'Auditors',
      value: summary.total_auditors ?? 0,
      subtitle: `${summary.active_auditors ?? 0} active`,
      color: '#7c3aed',
    },
    {
      label: 'Courses',
      value: summary.total_courses ?? 0,
      subtitle: `${summary.accredited_courses ?? 0} accredited`,
      color: colors.info,
    },
    {
      label: 'Pending Decisions',
      value: summary.pending_decisions ?? 0,
      subtitle: 'Requires action',
      color: summary.pending_decisions > 0 ? colors.warning : colors.success,
    },
    {
      label: 'Upcoming Surveillance',
      value: summary.upcoming_surveillance ?? 0,
      subtitle: 'Next 30 days',
      color: summary.upcoming_surveillance > 0 ? colors.warning : colors.textMuted,
    },
  ] : [];

  return (
    <div>
      <PageHeader
        title="Accreditation Management"
        subtitle="Manage certification body, auditor, and course accreditations."
      />

      {error && <Alert type="error" message={error} />}

      <KPIGrid kpis={kpis} columns={5} />

      {/* Quick Actions */}
      <div style={{ marginTop: spacing.xl }}>
        <div style={{
          fontSize: typography.fontSizes.xs,
          fontWeight: typography.fontWeights.semibold,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: spacing.md,
        }}>
          Quick Actions
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: spacing.sm }}>
          {QUICK_ACTIONS.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              style={{
                display: 'block',
                padding: `${spacing.md} ${spacing.lg}`,
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: radius.md,
                color: colors.textPrimary,
                textDecoration: 'none',
                fontSize: typography.fontSizes.sm,
                fontWeight: typography.fontWeights.medium,
                transition: 'border-color 150ms, color 150ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.color = colors.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.color = colors.textPrimary; }}
            >
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* Upcoming Surveillance */}
      <div style={{ marginTop: spacing.xl }}>
        <div style={{
          fontSize: typography.fontSizes.xs,
          fontWeight: typography.fontWeights.semibold,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: spacing.md,
        }}>
          Upcoming Surveillance Activities — Next 30 Days
        </div>
        <DataTable
          columns={SURVEILLANCE_COLS}
          rows={Array.isArray(surveillance) ? surveillance.slice(0, 10) : []}
          loading={survLoading}
          emptyMessage="No surveillance activities scheduled in the next 30 days."
          keyField="id"
        />
        {Array.isArray(surveillance) && surveillance.length > 10 && (
          <div style={{ marginTop: spacing.md, textAlign: 'center' }}>
            <Link to="/governance/surveillance" style={{ color: colors.primary, fontWeight: 600, textDecoration: 'none', fontSize: typography.fontSizes.sm }}>
              View all {surveillance.length} upcoming activities →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
